import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { subscribeWithRetry } from '../../hooks/useResilientSnapshot';
import db, {
  doc, collection, query, orderBy, addDoc, getDoc, updateDoc, setDoc, serverTimestamp,
} from '../../firebase/firestore';
import storage from '../../firebase/storage';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { ChatBubbleLeftRightIcon, XMarkIcon, PaperClipIcon, PaperAirplaneIcon, XCircleIcon } from '@heroicons/react/24/outline';

const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;

function uploadChatAttachment(academyId, file, onProgress) {
  return new Promise((resolve, reject) => {
    const path = `supportChats/${academyId}/${Date.now()}_${file.name}`;
    const task = uploadBytesResumable(storageRef(storage, path), file);
    task.on('state_changed', (snap) => onProgress(snap.bytesTransferred / snap.totalBytes), reject, async () => {
      resolve({ url: await getDownloadURL(task.snapshot.ref), filename: file.name, contentType: file.type || '' });
    });
  });
}

function Attachment({ a }) {
  if (a.contentType?.startsWith('image/')) {
    return <img src={a.url} alt={a.filename} style={{ maxWidth: '100%', borderRadius: 8, marginTop: 6, display: 'block' }} />;
  }
  if (a.contentType?.startsWith('video/')) {
    return <video src={a.url} controls style={{ maxWidth: '100%', borderRadius: 8, marginTop: 6, display: 'block' }} />;
  }
  return <a href={a.url} target="_blank" rel="noreferrer" style={{ display: 'block', marginTop: 6, fontSize: 12.5, textDecoration: 'underline' }}>{a.filename}</a>;
}

// Floating support-chat widget — the academy-side counterpart to
// super/ChatsInbox.jsx. Assigned per-academy (chatWidgetEnabled, toggled
// from TenantDetail.jsx), typically for a new academy's first month, so IAP
// can field questions/media directly rather than over email. Modeled on
// FeedbackButton.jsx's shell and ThreadPane.jsx's read/send logic, but
// against supportChats/{academyId} instead of conversations/{id}.
export default function ChatWidgetButton() {
  const { t } = useTranslation();
  const { user, userProfile, isAcademyAdmin, chatWidgetEnabled, academyId } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [chatDoc, setChatDoc] = useState(null);
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);

  const enabled = isAcademyAdmin && chatWidgetEnabled && academyId;

  useEffect(() => {
    if (!enabled) return;
    getDoc(doc(db, 'supportChats', academyId)).then((snap) => setChatDoc(snap.exists() ? snap.data() : null));
  }, [enabled, academyId, open]);

  useEffect(() => {
    if (!enabled || !open) return;
    const q = query(collection(db, 'supportChats', academyId, 'messages'), orderBy('createdAt', 'asc'));
    return subscribeWithRetry(q, (snap) => setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, [enabled, open, academyId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Marks the thread read for the academy side whenever the panel is open
  // and a message list comes in — same "opened it" / "arrived while open"
  // coverage as ThreadPane.jsx's identical effect.
  useEffect(() => {
    if (!enabled || !open || messages.length === 0) return;
    updateDoc(doc(db, 'supportChats', academyId), { lastReadAtAcademy: serverTimestamp() }).catch(() => {});
  }, [enabled, open, academyId, messages.length]);

  if (!enabled) return null;

  const hasUnread = !!(
    chatDoc?.lastMessage
    && chatDoc.lastMessage.senderRole === 'superAdmin'
    && (!chatDoc.lastReadAtAcademy || chatDoc.lastMessage.createdAt?.toMillis?.() > chatDoc.lastReadAtAcademy?.toMillis?.())
  );

  const handleFilesChosen = (e) => {
    const chosen = Array.from(e.target.files || []);
    const tooBig = chosen.find((f) => f.size > MAX_ATTACHMENT_BYTES);
    if (tooBig) {
      setError(t('common:components.chatWidget.fileTooBig', '"{{name}}" is over the 50MB limit.', { name: tooBig.name }));
      return;
    }
    setError('');
    setFiles((prev) => [...prev, ...chosen]);
    e.target.value = '';
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const handleSend = async (e) => {
    e.preventDefault();
    if ((!body.trim() && files.length === 0) || sending) return;
    setSending(true);
    setError('');
    const text = body.trim();
    try {
      const attachments = [];
      for (const file of files) {
        attachments.push(await uploadChatAttachment(academyId, file, () => {}));
      }
      const senderName = userProfile?.displayName || user.email || 'Academy';
      await addDoc(collection(db, 'supportChats', academyId, 'messages'), {
        senderRole: 'academy', senderId: user.uid, senderName, body: text, attachments, createdAt: serverTimestamp(),
      });
      await setDoc(doc(db, 'supportChats', academyId), {
        lastMessage: { body: text || (attachments.length ? t('common:components.chatWidget.attachmentPreview', '📎 Attachment') : ''), senderRole: 'academy', senderName, createdAt: serverTimestamp() },
        lastReadAtAcademy: serverTimestamp(),
      }, { merge: true });
      setBody('');
      setFiles([]);
    } catch (err) {
      setError(err.message || t('common:components.chatWidget.sendFailed', 'Failed to send. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-floating-bubble="support"
        onClick={() => setOpen(true)}
        aria-label={t('common:components.chatWidget.ariaLabel', 'Chat with support')}
        style={{
          position: 'fixed', right: 20, bottom: 84, zIndex: 1000,
          width: 48, height: 48, borderRadius: '50%',
          background: '#2563eb', color: '#fff', border: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)', cursor: 'pointer',
        }}
      >
        <ChatBubbleLeftRightIcon style={{ width: 22, height: 22 }} />
        {hasUnread && (
          <span style={{ position: 'absolute', top: -2, right: -2, width: 12, height: 12, borderRadius: '50%', background: '#ef4444', border: '2px solid #fff' }} />
        )}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[1001] flex items-end sm:items-center justify-center bg-black/40 px-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full sm:max-w-sm bg-white rounded-t-2xl sm:rounded-2xl flex flex-col"
            style={{ maxHeight: '80vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900">{t('common:components.chatWidget.title', 'Support chat')}</h3>
              <button onClick={() => setOpen(false)}><XMarkIcon className="h-5 w-5 text-gray-400" /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3" style={{ minHeight: 200 }}>
              <div className="rounded-lg bg-blue-50 text-blue-800 text-xs px-3 py-2">
                {t('common:components.chatWidget.greeting', 'Send us a message or request a video call if you are unsure about anything.')}
              </div>
              {messages.map((m) => {
                const mine = m.senderRole === 'academy';
                return (
                  <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      maxWidth: '80%', padding: '8px 12px', borderRadius: 12, fontSize: 13.5, lineHeight: 1.45,
                      background: mine ? '#2563eb' : '#f3f4f6', color: mine ? '#fff' : '#111827',
                    }}>
                      {!mine && <p style={{ fontSize: 11, fontWeight: 700, marginBottom: 2, opacity: 0.7 }}>{m.senderName || t('common:components.chatWidget.supportLabel', 'IAP Support')}</p>}
                      {m.body && <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</p>}
                      {(m.attachments || []).map((a, i) => <Attachment key={i} a={a} />)}
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {files.length > 0 && (
              <div className="px-5 flex flex-wrap gap-1.5 pb-2">
                {files.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-gray-100 rounded-full pl-2.5 pr-1 py-1 text-xs text-gray-600">
                    {f.name}
                    <button type="button" onClick={() => removeFile(i)}><XCircleIcon className="h-3.5 w-3.5 text-gray-400 hover:text-red-600" /></button>
                  </span>
                ))}
              </div>
            )}
            {error && <p className="px-5 text-xs text-red-600 pb-2">{error}</p>}

            <form onSubmit={handleSend} className="flex items-center gap-2 px-5 py-3 border-t border-gray-100">
              <label className="flex-shrink-0 text-gray-400 hover:text-brand-600 cursor-pointer">
                <PaperClipIcon className="h-5 w-5" />
                <input type="file" multiple accept="image/*,video/*" hidden onChange={handleFilesChosen} />
              </label>
              <input
                type="text" value={body} onChange={(e) => setBody(e.target.value)}
                placeholder={t('common:components.chatWidget.messagePlaceholder', 'Message…')}
                className="flex-1 rounded-full border border-gray-300 px-3.5 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
              <button
                type="submit" disabled={sending || (!body.trim() && files.length === 0)}
                className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center disabled:opacity-40"
                style={{ background: '#2563eb', color: '#fff' }}
              >
                <PaperAirplaneIcon className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
